import React, { createContext, useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import { getRequest, mednetAPI } from 'mednet-cns/src/api/v1'
import {
  FETCH_USER_WITH_PERMISSIONS,
  FETCH_USER_WITH_PERMISSIONS_PUBLIC,
} from 'mednet-cns/src/reducers/user'

const FLAGS_PATH = 'gregor/flags'

export const GregorFlagsContext = createContext({ values: {}, ready: false })

const selectUserAuth = createSelector(
  (state) => state.user && state.user.data,
  (state) => getRequest(state, FETCH_USER_WITH_PERMISSIONS),
  (state) => getRequest(state, FETCH_USER_WITH_PERMISSIONS_PUBLIC),
  (userData, userRequest, publicUserRequest) => ({
    userId: userData && userData.userId,
    isLoading: userRequest.isLoading || publicUserRequest.isLoading,
    isError: userRequest.isError || publicUserRequest.isError,
  })
)

export const GregorFlagsProvider = ({ children }) => {
  const { userId, isLoading, isError } = useSelector(selectUserAuth)
  const [state, setState] = useState({ values: {}, ready: false })

  useEffect(() => {
    if (isLoading) {
      setState({ values: {}, ready: false })
      return undefined
    }

    // Guests skip the auth-required proxy and fall back to defaults.
    if (isError || !userId) {
      setState({ values: {}, ready: true })
      return undefined
    }

    let cancelled = false
    setState((prev) => ({ ...prev, ready: false }))

    const fetchFlags = async () => {
      let values = {}

      const { response, isError: fetchError } = await mednetAPI(FLAGS_PATH)
      if (!fetchError && response) {
        try {
          values = await response.json()
        } catch (parseError) {
          values = {}
        }
      }

      if (!cancelled) {
        setState({ values: values || {}, ready: true })
      }
    }

    fetchFlags()

    return () => {
      cancelled = true
    }
  }, [userId, isLoading, isError])

  return (
    <GregorFlagsContext.Provider value={state}>
      {children}
    </GregorFlagsContext.Provider>
  )
}

export const useGregorFlag = (flagKey, defaultValue) => {
  const { values } = useContext(GregorFlagsContext)
  const value = values && values[flagKey]
  return value === undefined || value === null ? defaultValue : value
}

export const useGregorFlagsReady = () => {
  const { ready } = useContext(GregorFlagsContext)
  return ready
}
